// Public API: suggestJoinCandidates(scope, snapshot, leftTable) → JoinCandidate[].
// Pure function. Given a "left" table already in the FROM/JOIN list, returns
// every FK-derived JOIN candidate reachable through SchemaSnapshot.foreignKeys.
// FK edges in either direction produce candidates; `fkEdge.direction` tells the
// caller which side is the PK side.
//
// Signature follows the established suggest-* shape (snapshot threaded as the
// second positional arg — Scope carries no SchemaSnapshot reference). See
// PROGRESS.md Codebase Patterns.
//
// Alias derivation: word-initials from the right table's name. Word boundaries
// are the first char, any char following `_` / `-` / `.`, and any uppercase
// char (CamelCase). Result lowercased. Collisions against already-used aliases
// (scope.aliases across the parent chain + candidates already emitted on this
// call) are disambiguated by appending the lowest integer ≥ 2.
//
// Snippet body (Monaco/LSP-compatible): `schema.table ${1:alias} ON ${1}.right_col
// = left_alias.left_col$0`; multi-column FKs AND-chain. The left side is the
// query's own spelling of the left table — its alias (delimited when it must
// be), else its name token as written (`"order items"` stays delimited). The
// catalog names stay dialect-agnostic (PRD: parity-required snippet shape);
// the VQE writes its own dialect-quoted ON (`formatJoinOnText`). The left table
// is matched by the snapshot's fold of its NAME whatever the query's quoting
// (PostgreSQL `"VqeMiMixed"` is keyed `vqemimixed`).
//
// Loose FKs (plain, JSON-path, filtered) surface as JOIN candidates too — that
// pass lives in `looseRelJoinCandidates.ts` (200-line cap), sharing the
// alias/snippet helpers below. PRD: Ralph/SemanticResolverReadOnly/PRD.md §
// Slice: Suggestion — Join Candidates (FK-driven).

import type { QualifiedName, SqlEngine } from '../../sql-grammar/model';
import { foldFor } from '../service/schemaProjectionHelpers';
import type {
  AliasBinding,
  FkDirection,
  FkEdge,
  JoinCandidate,
  SchemaSnapshot,
  Scope,
  Table,
} from '../types';
import { appendLooseRelJoinCandidates } from './looseRelJoinCandidates';
import { lookupRelationTable, resolvedRelationName } from '../service/snapshotRelations';
import { aliasSql, boundLeafSql } from './sqlIdentifierText';

export function suggestJoinCandidates(
  scope: Scope,
  snapshot: SchemaSnapshot | undefined,
  leftTable: QualifiedName,
): JoinCandidate[] {
  const looseRels = snapshot?.looseRelationships ?? [];
  if (!snapshot || (snapshot.foreignKeys.length === 0 && looseRels.length === 0)) {
    return [];
  }

  const engine = snapshot.engineId as SqlEngine;
  const alias = findAliasForTable(scope, leftTable);
  const leftAliasName = alias
    ? aliasSql(alias.alias, alias.aliasFolded, engine)
    : boundLeafSql(leftTable, engine);
  const usedAliases = collectUsedAliases(scope);
  // Edges are matched against the RELATION the left reference names (schema
  // included), looked up by the snapshot's fold of its name whatever the
  // query's quoting: `FROM orders` is the search-path / default-schema
  // `orders`, so another schema's same-named table never lends it its FK
  // edges. An ambiguous bare name (several `orders`, none on the default path)
  // offers no candidates rather than another schema's.
  const leftRelation = resolvedRelationName(snapshot, snapshotKeyOf(leftTable, engine));
  if (!leftRelation) return [];

  const out: JoinCandidate[] = [];
  for (const fk of snapshot.foreignKeys) {
    for (const direction of classifyEdgeDirections(leftRelation, fk)) {
      const rightQN = direction === 'left-references-right' ? fk.toTable : fk.fromTable;
      const rightColumns = direction === 'left-references-right' ? fk.toColumns : fk.fromColumns;
      const leftColumns = direction === 'left-references-right' ? fk.fromColumns : fk.toColumns;
      if (leftColumns.length === 0 || rightColumns.length !== leftColumns.length) continue;

      const rightTable: Table = lookupRelationTable(snapshot, rightQN) ?? {
        qualified: rightQN,
        kind: 'table',
      };

      const base = deriveShortAlias(rightQN.name);
      const suggestedAlias = disambiguateAlias(base, usedAliases);
      usedAliases.add(suggestedAlias.toLowerCase());

      const snippet = buildJoinSnippet(rightQN, suggestedAlias, rightColumns, leftColumns, leftAliasName);

      out.push({
        leftTable,
        rightTable,
        fkEdge: { name: fk.name, leftColumns, rightColumns, direction },
        suggestedAlias,
        snippet,
      });
    }
  }

  appendLooseRelJoinCandidates(out, snapshot, looseRels, leftTable, leftRelation, leftAliasName, usedAliases, {
    qualifiedNameMatches,
    deriveShortAlias,
    disambiguateAlias,
    buildJoinSnippet,
  });
  return out;
}

// Self-referencing FK edges (fromTable === toTable) surface both directions —
// the user might join to the PK side or the FK side. Non-self-ref FKs only
// ever match one side of the leftTable comparison.
function classifyEdgeDirections(leftTable: QualifiedName, fk: FkEdge): FkDirection[] {
  const dirs: FkDirection[] = [];
  if (qualifiedNameMatches(leftTable, fk.fromTable)) dirs.push('left-references-right');
  if (qualifiedNameMatches(leftTable, fk.toTable)) dirs.push('right-references-left');
  return dirs;
}

// The snapshot's key for a query-bound name: its fold of the name (and schema),
// independent of how the query quoted it.
function snapshotKeyOf(qualified: QualifiedName, engine: SqlEngine): QualifiedName {
  const fold = foldFor(engine);
  return {
    ...qualified,
    foldedName: fold(qualified.name),
    ...(qualified.schema ? { foldedSchema: fold(qualified.schema) } : {}),
  };
}

// Name-first match with optional schema tightening. If both sides have
// foldedSchema, require schema match; otherwise fall back to name-only. This
// mirrors how users refer to tables: `SELECT … FROM c` matches any schema's
// table `c`, but `SELECT … FROM dbo.c` should match only dbo.
function qualifiedNameMatches(a: QualifiedName, b: QualifiedName): boolean {
  if (a.foldedName !== b.foldedName) return false;
  const as = a.foldedSchema;
  const bs = b.foldedSchema;
  if (as && bs) return as === bs;
  return true;
}

function findAliasForTable(scope: Scope, leftTable: QualifiedName): AliasBinding | undefined {
  let s: Scope | undefined = scope;
  while (s) {
    for (const a of s.aliases) {
      if (a.source === 'pseudo') continue;
      if (aliasTargetsTable(a, leftTable)) return a;
    }
    s = s.parent;
  }
  return undefined;
}

function aliasTargetsTable(a: AliasBinding, leftTable: QualifiedName): boolean {
  if (a.target.foldedName !== leftTable.foldedName) return false;
  const as = a.target.foldedSchema;
  const bs = leftTable.foldedSchema;
  if (as && bs) return as === bs;
  return true;
}

function collectUsedAliases(scope: Scope): Set<string> {
  const used = new Set<string>();
  let s: Scope | undefined = scope;
  while (s) {
    for (const a of s.aliases) used.add(a.alias.toLowerCase());
    s = s.parent;
  }
  return used;
}

/** Initials-style alias for a table name (`order_items` → `oi`) — shared with the VQE's base-table aliasing. */
export function deriveShortAlias(name: string): string {
  const chars: string[] = [];
  let prevWasBoundary = true;
  for (let i = 0; i < name.length; i++) {
    const c = name[i];
    if (c === '_' || c === '-' || c === '.' || c === ' ') {
      prevWasBoundary = true;
      continue;
    }
    const isUpper = c >= 'A' && c <= 'Z';
    if (prevWasBoundary || isUpper) chars.push(c.toLowerCase());
    prevWasBoundary = false;
  }
  if (chars.length > 0) return chars.join('');
  for (const c of name) {
    const isAlpha = (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');
    if (isAlpha) return c.toLowerCase();
  }
  return 't';
}

/** `base`, or `base2`, `base3`… — the first not in `used` (folded). */
export function disambiguateAlias(base: string, used: ReadonlySet<string>): string {
  const lower = base.toLowerCase();
  if (!used.has(lower)) return lower;
  let i = 2;
  while (used.has(`${lower}${i}`)) i++;
  return `${lower}${i}`;
}

function buildJoinSnippet(
  rightQN: QualifiedName,
  suggestedAlias: string,
  rightColumns: readonly string[],
  leftColumns: readonly string[],
  leftAliasName: string,
): string {
  const schemaPrefix = rightQN.schema ? `${rightQN.schema}.` : '';
  const tableRef = `${schemaPrefix}${rightQN.name}`;
  const conds: string[] = [];
  for (let i = 0; i < rightColumns.length; i++) {
    conds.push(`\${1}.${rightColumns[i]} = ${leftAliasName}.${leftColumns[i]}`);
  }
  return `${tableRef} \${1:${suggestedAlias}} ON ${conds.join(' AND ')}$0`;
}
